/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Add Favorite Code component
 */
import _ from 'lodash';
import { Button, message, Tooltip } from 'antd';
import { Star } from '@icon-park/react';
import { TooltipPlacement } from 'antd/lib/tooltip';

import EventTracker from 'utils/eventTracker';
import useDeleteCodeTenantData from 'hooks/codeExplorer/mutate/useDeleteCodeTenantData';
import usePostCodeTenantData from 'hooks/codeExplorer/mutate/usePostCodeTenantData';
import { EVENTS } from 'app-constants/events';
import { REACTION_KEYS } from 'app-constants/codeExplorer';

type IProps = {
	code: string;
	codeSet: CodeExplorer.ICodeSetSystem;
	codeId: string;
	entity?: string;
	reactions: CodeExplorer.ICodeReactionModel[];
	refetch?: () => void;
	showOnlyIcon?: boolean;
	placement?: TooltipPlacement;
};

const AddFavoriteCode: React.FC<IProps> = ({
	code,
	codeSet,
	entity,
	codeId,
	reactions,
	refetch,
	showOnlyIcon = false,
	placement = 'top',
}) => {
	const addFavourite = usePostCodeTenantData();
	const deleteFavourite = useDeleteCodeTenantData();

	const favouriteReaction = _.find(reactions, (item) => item?.reaction === REACTION_KEYS.favourite);
	const isFavourite = !!favouriteReaction;

	const tooltipTitle = isFavourite ? 'Remove from Favorites' : 'Add to Favorites';

	const handleClick = () => {
		if (isFavourite) {
			deleteFavourite.mutate(
				{ codeId, reaction: [favouriteReaction?.id] },
				{
					onSuccess: () => {
						refetch?.();
						message.success('Removed from Favorites');
						EventTracker.trackEvent(EVENTS.codeExplorer.favoriteDeleted(code, codeSet, entity));
					},
				},
			);
		} else {
			addFavourite.mutate(
				{ codeId, reaction: [{ reaction: REACTION_KEYS.favourite as CodeExplorer.ICodeReactionModel['reaction'] }] },
				{
					onSuccess: () => {
						refetch?.();
						message.success('Added to Favorites');
						EventTracker.trackEvent(EVENTS.codeExplorer.favoriteAdded(code, codeSet, entity));
					},
				},
			);
		}
	};
	// only show icon if it is a favourite
	if (showOnlyIcon && isFavourite) {
		return <Star theme='filled' size='16' fill='#EC9A29' />;
	}
	// if not a favourite and showOnlyIcon is true, return null
	if (showOnlyIcon) {
		return null;
	}
	return (
		<Tooltip title={tooltipTitle} placement={placement}>
			<Button
				data-testid='ce-favorite-button'
				type='text'
				icon={<Star theme={isFavourite ? 'filled' : 'outline'} size={16} fill={isFavourite ? '#EC9A29' : '#8C8C8C'} />}
				onClick={handleClick}
			/>
		</Tooltip>
	);
};

export default AddFavoriteCode;
